/**
 * 添加直播场次表单组件
 */

import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { createSession } from '@/lib/api';
import type { AddSessionForm } from '@/types';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { normalizeBilibiliLiveUrl } from '@/lib/utils';

interface AddRoomFormProps {
  onSessionAdded: () => void;
}

export default function AddRoomForm({ onSessionAdded }: AddRoomFormProps) {
  const [roomUrl, setRoomUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roomUrl.trim()) {
      setError('请输入直播间 URL 或房间号');
      return;
    }

    // 规范化URL，提取房间号
    const normalizedUrl = normalizeBilibiliLiveUrl(roomUrl);
    
    if (!normalizedUrl) {
      setError('请输入有效的 B站直播间链接或房间号\n支持格式：\n• https://live.bilibili.com/123456\n• https://live.bilibili.com/h5/123456\n• 纯房间号：123456');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      // 使用规范化后的URL调用创建场次API，不设置预定时间
      const formData: AddSessionForm = {
        room_url: normalizedUrl,
        scheduled_start_time: '', // 不设置预定时间，由后端自动处理
      };
      await createSession(formData);

      // 成功后重置表单
      setRoomUrl('');
      setSuccess(true);

      // 通知父组件刷新列表
      onSessionAdded();

      // 3秒后隐藏成功提示
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('创建场次失败:', err);
      setError(err instanceof Error ? err.message : '添加失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoomUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRoomUrl(e.target.value);
    setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="room_url" className="mb-2 flex items-center gap-2">
          直播间 URL <span className="text-[var(--status-error)]">*</span>
        </Label>
        <Input
          type="text"
          id="room_url"
          value={roomUrl}
          onChange={handleRoomUrlChange}
          placeholder="支持多种格式：链接、房间号等"
          disabled={isSubmitting}
          className="w-full"
        />
        <p className="mt-1 text-xs leading-5 text-[var(--text-muted)]">
          支持桌面链接、H5链接、纯房间号等多种格式，系统将自动识别并获取主播信息
        </p>
      </div>

      <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--panel-deep)] px-4 py-3">
        <p className="mb-2 text-sm font-medium text-[var(--text-soft)]">智能录制说明</p>
        <div className="space-y-1 text-xs leading-5 text-[var(--text-muted)]">
          <p>• 如果直播<span className="font-medium">正在进行</span>，创建后会<span className="font-medium">立即开始</span>录制</p>
          <p>• 如果直播<span className="font-medium">尚未开始</span>，系统会自动等待，<span className="font-medium">开播后自动开始</span>录制</p>
          <p>• 每个场次最长运行4小时后自动停止</p>
        </div>
        <p className="mt-2 text-xs font-medium text-[var(--status-warning)]">创建前请确认设置页显示有效授权。</p>
      </div>

      {error && (
        <div className="console-alert-error px-4 py-3 text-sm">
          <p className="font-medium">创建失败</p>
          <p className="mt-1">{error}</p>
        </div>
      )}

      {success && (
        <div className="console-alert-success px-4 py-3 text-sm">
          <p className="font-medium">创建成功</p>
          <p className="mt-1">场次已创建，系统将自动开始监控</p>
        </div>
      )}

      <Button type="submit" disabled={isSubmitting} className="min-h-11 w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>正在创建场次...</span>
          </>
        ) : (
          <>
            <Plus className="h-4 w-4" />
            <span>创建直播场次</span>
          </>
        )}
      </Button>
    </form>
  );
}
